
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';

const TermsOfService = () => {
  return (
    <>
      <SEOHead
        title="Terms of Service | Milky Way Planner"
        description="Terms of service for using Milky Way Planner, including accounts, Premium subscriptions, acceptable use, and the rules that apply when you use the site."
        keywords="terms of service, milky way planner terms, astrophotography tool terms"
        canonicalUrl="/terms"
      />
      <div className="min-h-screen bg-background">
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Legal</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Terms of Service
            </h1>
            <p className="text-sm text-white/50">Last updated: June 8, 2025</p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-3xl space-y-8 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By accessing and using Milky Way Planner, you accept and agree to be bound by the terms and provision of this agreement.
                If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. Description of Service</h2>
              <p className="text-muted-foreground">
                Milky Way Planner is an astrophotography planning tool that provides astronomical data including Milky Way visibility,
                moon phases, sunrise/sunset times, and other celestial information to help photographers plan their sessions.
                The service is provided "as is" and we make no guarantees about the accuracy of astronomical calculations.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. User Accounts</h2>
              <p className="text-muted-foreground mb-3">
                To access certain features of our service, you may be required to create an account. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Maintaining the confidentiality of your account information</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and complete information</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Subscription and Payment</h2>
              <p className="text-muted-foreground">
                Premium features require a paid subscription. Subscriptions are billed annually.
                You may cancel your subscription at any time through your account settings. Refunds are handled according to our refund policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Acceptable Use</h2>
              <p className="text-muted-foreground mb-3">
                You agree not to use the service to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Violate any applicable laws or regulations</li>
                <li>Interfere with or disrupt the service</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated tools to access the service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                Milky Way Planner shall not be liable for any direct, indirect, incidental, special, or consequential damages
                resulting from the use or inability to use the service, including but not limited to damages for loss of profits,
                goodwill, use, data, or other intangible losses.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these terms at any time. Changes will be posted on this page with an updated
                revision date. Your continued use of the service after such changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Contact Information</h2>
              <p className="text-muted-foreground">
                If you have any questions about these Terms of Service, please contact us through our
                <Link to="/contact" className="text-primary hover:text-primary/80 ml-1">contact page</Link>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
